import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../../services/api';
import { Filter, ClipboardList, User, ChevronLeft, ChevronRight, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { TYPE_LABELS, Spinner } from './shared';

export function AuditTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [staffOptions, setStaffOptions] = useState([]);
  const [filterStaff, setFilterStaff] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterDays, setFilterDays] = useState(30);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50, days: filterDays });
      if (filterStaff) params.set('staff_id', filterStaff);
      if (filterType) params.set('action_type', filterType);
      const res = await adminApi.get(`/admin/audit-log?${params}`);
      setEntries(res.data.entries);
      setTotalPages(res.data.pages);
      setTotal(res.data.total);
      if (res.data.staff_options) setStaffOptions(res.data.staff_options);
    } catch {} finally { setLoading(false); }
  }, [page, filterStaff, filterType, filterDays]);
  useEffect(() => { fetchLog(); }, [fetchLog]);

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div data-testid="audit-log-tab">
      <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm mb-4">
        <div className="flex items-center gap-2 mb-3"><Filter size={14} className="text-[#D44E20]" /><span className="text-xs text-[#64748B] font-medium">Filtra</span></div>
        <div className="flex flex-wrap gap-3">
          <select value={filterStaff} onChange={e => { setFilterStaff(e.target.value); setPage(1); }}
            className="h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none">
            <option value="">Tutti gli operatori</option>
            {staffOptions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={filterType} onChange={e => { setFilterType(e.target.value); setPage(1); }}
            className="h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none">
            <option value="">Tutte le azioni</option>
            {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={filterDays} onChange={e => { setFilterDays(Number(e.target.value)); setPage(1); }}
            className="h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none">
            {[7, 30, 90, 365].map(d => <option key={d} value={d}>{d <= 90 ? `Ultimi ${d} giorni` : 'Ultimo anno'}</option>)}
          </select>
          <span className="text-[#64748B] text-xs self-center ml-auto">{total} operazioni</span>
        </div>
      </div>
      {loading ? <Spinner /> : entries.length === 0 ? (
        <div className="text-center py-16"><ClipboardList size={40} className="mx-auto text-gray-300 mb-3" /><p className="text-[#64748B] text-sm">Nessuna operazione trovata</p></div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="audit-table">
              <thead><tr className="border-b border-slate-100">
                {['Data/ora', 'Tipo', 'Operatore', 'Cliente', 'Punti', 'Nota'].map(h => (
                  <th key={h} className={`${h === 'Punti' ? 'text-right' : 'text-left'} px-4 py-3 text-[#64748B] text-xs font-medium`}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {entries.map(e => {
                  const meta = TYPE_LABELS[e.type] || { label: e.type, color: '#888' };
                  return (
                    <tr key={e.id} data-testid={`audit-row-${e.id}`} className="border-b border-gray-50 hover:bg-slate-50/50">
                      <td className="px-4 py-3 text-sm tabular-nums text-[#1A1A1A]">{fmtDate(e.created_at)}</td>
                      <td className="px-4 py-3"><span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: `${meta.color}15`, color: meta.color }}>{meta.label}</span></td>
                      <td className="px-4 py-3"><div className="flex items-center gap-2"><div className="w-6 h-6 bg-[#D44E20]/10 rounded-full flex items-center justify-center"><User size={10} className="text-[#D44E20]" /></div><span className="text-sm text-[#1A1A1A]">{e.staff_name}</span></div></td>
                      <td className="px-4 py-3 text-sm text-[#1A1A1A]">{e.customer_name}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`text-sm font-bold tabular-nums ${e.points_delta > 0 ? 'text-[#3ECF8E]' : 'text-[#EF4444]'}`}>
                          {e.points_delta > 0 ? <ArrowUpRight size={12} className="inline" /> : <ArrowDownRight size={12} className="inline" />}
                          {e.points_delta > 0 ? '+' : ''}{e.points_delta}
                        </span>
                        {e.amount_eur && <span className="text-[#64748B] text-xs block">{'\u20AC'}{e.amount_eur.toFixed(2)}</span>}
                      </td>
                      <td className="px-4 py-3 text-[#64748B] text-xs">{e.note || '\u2014'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <span className="text-[#64748B] text-xs">Pagina {page} di {totalPages}</span>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-50 text-[#64748B] hover:bg-slate-100 disabled:opacity-30"><ChevronLeft size={14} /></button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-50 text-[#64748B] hover:bg-slate-100 disabled:opacity-30"><ChevronRight size={14} /></button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
